(() => {
  const containerSelector = '#ProductGridContainer';
  const gridSelector = '#product-grid';
  const markerSelector = '[data-ferreti-infinite-scroll]';
  const loadedPages = new Set();
  const loadedProductIds = new Set();
  let observer = null;
  let mutationObserver = null;
  let activeMarker = null;
  let isLoading = false;
  const preloadOffset = 650;
  let scrollTicking = false;

  const normalizePageUrl = (url) => {
    const pageUrl = new URL(url, window.location.origin);
    pageUrl.searchParams.delete('section_id');
    return `${pageUrl.pathname}${pageUrl.search}${pageUrl.hash}`;
  };

  const getSectionUrl = (url, sectionId) => {
    const nextUrl = new URL(url, window.location.origin);
    nextUrl.searchParams.set('section_id', sectionId);
    return nextUrl.toString();
  };

  const setMessage = (marker, text) => {
    const message = marker.querySelector('.ferreti-infinite-scroll__message');
    if (message) message.textContent = text;
  };

  const getRenderedCount = (grid) => grid?.querySelectorAll('[data-product-id]').length || 0;

  const getTargetCount = (marker) => {
    const targetCount = Number.parseInt(marker?.dataset.totalCount || '', 10);
    return Number.isFinite(targetCount) ? targetCount : 0;
  };

  const getPageNumber = (url) => {
    const parsedUrl = new URL(url, window.location.origin);
    return Number.parseInt(parsedUrl.searchParams.get('page') || '1', 10) || 1;
  };

  const getMarkerCurrentPage = (marker) => Number.parseInt(marker?.dataset.currentPage || '1', 10) || 1;

  const getMarkerTotalPages = (marker) => Number.parseInt(marker?.dataset.totalPages || '1', 10) || 1;

  const buildPageUrl = (pageNumber) => {
    const baseUrl = new URL(window.location.href, window.location.origin);
    baseUrl.searchParams.delete('section_id');

    if (pageNumber <= 1) {
      baseUrl.searchParams.delete('page');
    } else {
      baseUrl.searchParams.set('page', String(pageNumber));
    }

    return `${baseUrl.pathname}${baseUrl.search}${baseUrl.hash}`;
  };

  const incrementPageUrl = (url) => {
    const parsedUrl = new URL(url, window.location.origin);
    parsedUrl.searchParams.set('page', String(getPageNumber(url) + 1));
    parsedUrl.searchParams.delete('section_id');
    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  };

  const markerNeedsMoreLoading = (marker) => {
    const rect = marker.getBoundingClientRect();
    return rect.top <= window.innerHeight + preloadOffset;
  };

  const tryLoadVisibleMarker = () => {
    const marker = document.querySelector(markerSelector);
    if (!marker || marker.dataset.complete === 'true' || !marker.dataset.nextUrl) return;
    if (isLoading) return;
    if (markerNeedsMoreLoading(marker)) loadNextPage(marker);
  };

  const markComplete = (marker) => {
    marker.dataset.complete = 'true';
    marker.dataset.nextUrl = '';
    marker.classList.remove('is-loading');
    setMessage(marker, 'Ya viste todos los productos disponibles.');
  };

  const initScrollAnimation = (rootEl) => {
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(rootEl);
    }
  };

  const getProductKey = (item) => {
    if (item.dataset.productId) return item.dataset.productId;

    const productLink = item.querySelector('a[href*="/products/"]');
    if (!productLink) return '';

    try {
      const productUrl = new URL(productLink.getAttribute('href'), window.location.origin);
      return productUrl.pathname;
    } catch (error) {
      return productLink.getAttribute('href') || '';
    }
  };

  const rememberExistingProducts = (grid) => {
    loadedProductIds.clear();
    Array.from(grid.children).forEach((item) => {
      const productKey = getProductKey(item);
      if (productKey) loadedProductIds.add(productKey);
    });
  };

  const fetchAndAppendPage = async (marker, grid, sectionId, pageUrl) => {
    const normalizedPageUrl = normalizePageUrl(pageUrl);

    if (loadedPages.has(normalizedPageUrl)) {
      return {
        appendedCount: 0,
        totalPages: getMarkerTotalPages(marker),
      };
    }

    loadedPages.add(normalizedPageUrl);

    const response = await fetch(getSectionUrl(pageUrl, sectionId), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!response.ok) {
      loadedPages.delete(normalizedPageUrl);
      throw new Error(`Collection page request failed: ${response.status}`);
    }

    const responseText = await response.text();
    const parsedDocument = new DOMParser().parseFromString(responseText, 'text/html');
    const nextGrid = parsedDocument.querySelector(gridSelector);
    const nextMarker = parsedDocument.querySelector(markerSelector);
    const nextItems = nextGrid ? Array.from(nextGrid.children) : [];
    const fetchedPageNumber = getPageNumber(normalizedPageUrl);
    const totalPages = Math.max(getMarkerTotalPages(marker), getMarkerTotalPages(nextMarker), fetchedPageNumber);
    let appendedCount = 0;

    nextItems.forEach((item) => {
      const productKey = getProductKey(item);
      if (productKey && loadedProductIds.has(productKey)) return;
      if (productKey) loadedProductIds.add(productKey);
      grid.appendChild(document.importNode(item, true));
      appendedCount += 1;
    });

    marker.dataset.currentPage = String(fetchedPageNumber);
    marker.dataset.totalPages = String(totalPages);
    initScrollAnimation(grid);

    return {
      appendedCount,
      totalPages,
    };
  };

  const loadNextPage = async (marker) => {
    const grid = document.querySelector(gridSelector);
    const sectionId = marker.dataset.sectionId || grid?.dataset.id;
    const currentPage = getMarkerCurrentPage(marker);
    const totalPages = getMarkerTotalPages(marker);
    const targetCount = getTargetCount(marker);
    let nextPage = currentPage + 1;

    if (!grid || !sectionId || isLoading || marker.dataset.complete === 'true') return;
    if (targetCount > 0 && getRenderedCount(grid) >= targetCount) {
      markComplete(marker);
      return;
    }
    if (nextPage > totalPages) {
      markComplete(marker);
      return;
    }
    if (!loadedProductIds.size) rememberExistingProducts(grid);

    isLoading = true;
    marker.classList.add('is-loading');
    setMessage(marker, 'Cargando mas productos...');

    try {
      let knownTotalPages = totalPages;

      while (nextPage <= knownTotalPages) {
        const renderedBefore = getRenderedCount(grid);
        const pageUrl = buildPageUrl(nextPage);
        const { appendedCount, totalPages: fetchedTotalPages } = await fetchAndAppendPage(marker, grid, sectionId, pageUrl);
        const renderedAfter = getRenderedCount(grid);
        knownTotalPages = Math.max(knownTotalPages, fetchedTotalPages);
        marker.dataset.nextUrl = nextPage < knownTotalPages ? buildPageUrl(nextPage + 1) : '';
        marker.dataset.complete = marker.dataset.nextUrl ? 'false' : 'true';
        nextPage += 1;

        if (appendedCount === 0 && renderedAfter === renderedBefore) {
          const fallbackUrl = incrementPageUrl(pageUrl);
          const normalizedFallbackUrl = normalizePageUrl(fallbackUrl);

          if (!loadedPages.has(normalizedFallbackUrl)) {
            nextPage = getPageNumber(fallbackUrl);
            continue;
          }
        }

        if (targetCount > 0 && renderedAfter >= targetCount) {
          markComplete(marker);
          break;
        }

        if (!markerNeedsMoreLoading(marker) && renderedAfter > renderedBefore) {
          break;
        }
      }

      if (nextPage > knownTotalPages && (targetCount === 0 || getRenderedCount(grid) >= targetCount)) {
        markComplete(marker);
      }
    } catch (error) {
      markComplete(marker);
      console.warn(error);
    } finally {
      isLoading = false;
      marker.classList.remove('is-loading');

      if (
        marker.dataset.complete !== 'true'
        && marker.dataset.nextUrl
        && (
          markerNeedsMoreLoading(marker)
          || (targetCount > 0 && getRenderedCount(grid) < targetCount)
        )
      ) {
        window.setTimeout(() => loadNextPage(marker), 80);
      }
    }
  };

  const bindInfiniteScroll = () => {
    const marker = document.querySelector(markerSelector);

    if (!marker || marker === activeMarker) return;
    if (activeMarker && marker !== activeMarker) {
      loadedPages.clear();
      const grid = document.querySelector(gridSelector);
      if (grid) rememberExistingProducts(grid);
    }
    activeMarker = marker;
    isLoading = false;

    if (observer) observer.disconnect();

    if (marker.dataset.complete === 'true' || !marker.dataset.nextUrl) {
      markComplete(marker);
      return;
    }

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) loadNextPage(marker);
        });
      },
      { rootMargin: `${preloadOffset}px 0px` }
    );

    observer.observe(marker);

    if (markerNeedsMoreLoading(marker)) {
      window.setTimeout(() => loadNextPage(marker), 60);
    }
  };

  const watchCollectionRerenders = () => {
    const container = document.querySelector(containerSelector);
    if (!container || mutationObserver) return;

    mutationObserver = new MutationObserver(() => {
      bindInfiniteScroll();
    });

    mutationObserver.observe(container, { childList: true, subtree: true });
  };

  const init = () => {
    bindInfiniteScroll();
    watchCollectionRerenders();
    window.addEventListener(
      'scroll',
      () => {
        if (scrollTicking) return;
        scrollTicking = true;
        window.requestAnimationFrame(() => {
          tryLoadVisibleMarker();
          scrollTicking = false;
        });
      },
      { passive: true }
    );
    window.addEventListener('resize', tryLoadVisibleMarker, { passive: true });
    window.setTimeout(tryLoadVisibleMarker, 120);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
